import * as React from 'react';
import BPromise from 'bluebird';
import { History, Location } from 'history';
import { parse } from 'query-string';
import { Grid2 } from 'Src/components/Grid/Grid2';
import { Loader } from 'Src/components/Loader/Loader';
import { ActionItem } from 'Src/components/ActionsButton/ActionsButton';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { DATE_INTERVAL, FILTER_TYPE, FIRST_PAGE } from 'Src/consts/table';
import {
  ColumnDefinition,
  getFilters2,
  getOrder,
  getSearchFilter,
  getSearchOrder,
  getServerFieldSectionWhere,
  getServerQueryFilter2,
  isFilterExist2,
  isSortExist,
  TABLE_SORT_DIRECTION
} from 'Src/helpers/table/table';
import { SchoolHouse } from 'Src/models/house';
import {
  getAllSuperAdminSchoolHouses,
  createSuperAdminSchoolHouse,
  getSuperAdminSchoolHouses,
  getSuperAdminSchoolHousesCount
} from 'Src/helpers/service/superadmin/houses';
import { uploadSuperAdminImage } from 'Src/helpers/service/image';

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Name',
    field: 'name',
    isSort: false,
    type: FILTER_TYPE.TEXT,
    accessor: ['name']
  },
  {
    text: 'Description',
    field: 'description',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: ['description']
  },
  {
    text: 'Status',
    field: 'status',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: ['status']
  }
];

interface Props {
  schoolId: string;
  history: History;
  location: Location;
}

interface State {
  items: SchoolHouse[];
  currentPage: number;
  selectedItems: SchoolHouse[];
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  filters: any;
  isDataFiltered: boolean;
  isLoading: boolean;
  total: number;
  basePath: string;
  isSelectAllChecked: boolean;
  errorMessage: string;
  isCreateModalOpen: boolean;
  isCreating: boolean;
  createName: string;
  createDescription: string;
  createColors: string[];
  createPic: string;
  createPicPreview: string;
  createErrorMessage: string;
}

const STATUS_OPTIONS = [
  { text: 'Active', value: 'ACTIVE' },
  { text: 'Archived', value: 'ARCHIVED' },
  { text: 'Removed', value: 'REMOVED' },
  { text: 'Deleted', value: 'DELETED' }
];

export class SuperAdminSchoolHouses extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      items: [],
      currentPage: FIRST_PAGE,
      selectedItems: [],
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      filters: {},
      isDataFiltered: false,
      isLoading: false,
      total: 0,
      basePath: '',
      isSelectAllChecked: false,
      errorMessage: '',
      isCreateModalOpen: false,
      isCreating: false,
      createName: '',
      createDescription: '',
      createColors: ['#1e88e5'],
      createPic: '',
      createPicPreview: '',
      createErrorMessage: ''
    };
  }

  openCreateModal = () => {
    this.setState({
      isCreateModalOpen: true,
      isCreating: false,
      createName: '',
      createDescription: '',
      createColors: ['#1e88e5'],
      createPic: '',
      createPicPreview: '',
      createErrorMessage: ''
    });
  };

  closeCreateModal = () => {
    if (!this.state.isCreating) {
      this.setState({ isCreateModalOpen: false, createErrorMessage: '' });
    }
  };

  onCreateColorChange = (index: number, color: string) => {
    const createColors = [...this.state.createColors];
    createColors[index] = color;
    this.setState({ createColors });
  };

  addCreateColor = () => {
    if (this.state.createColors.length < 2) {
      this.setState({ createColors: [...this.state.createColors, '#ffffff'] });
    }
  };

  removeCreateColor = (index: number) => {
    this.setState({ createColors: this.state.createColors.filter((_, colorIndex) => colorIndex !== index) });
  };

  onCreatePicChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';

    if (!file) return;
    if (!file.type.startsWith('image/')) {
      this.setState({ createErrorMessage: 'Please select an image file.' });
      return;
    }

    const createPicPreview = URL.createObjectURL(file);
    this.setState({ isCreating: true, createPicPreview, createErrorMessage: '' });

    uploadSuperAdminImage(file)
      .then(response => {
        const createPic =
          response.link ||
          response.picUrl ||
          response.url ||
          (response.key ? `${window.apiImg}/images/${response.key}` : '');

        if (!createPic) {
          throw new Error('Image upload returned no URL.');
        }

        this.setState({ isCreating: false, createPic });
      })
      .catch(() => {
        this.setState({
          isCreating: false,
          createPic: '',
          createPicPreview: '',
          createErrorMessage: 'Unable to upload the house emblem.'
        });
      });
  };

  createHouse = () => {
    const name = this.state.createName.trim();

    if (!name) {
      this.setState({ createErrorMessage: 'House name is required.' });
      return;
    }

    this.setState({ isCreating: true, createErrorMessage: '' });

    createSuperAdminSchoolHouse(this.props.schoolId, {
      name,
      description: this.state.createDescription.trim(),
      colors: this.state.createColors,
      ...(this.state.createPic ? { pic: this.state.createPic } : {})
    })
      .then(() => {
        this.setState({
          isCreateModalOpen: false,
          isCreating: false,
          selectedItems: [],
          isSelectAllChecked: false
        });
        return this.setItems();
      })
      .catch(error => {
        const createErrorMessage =
          error?.response?.data?.details?.text || error?.message || 'Unable to create house.';
        this.setState({ isCreating: false, createErrorMessage });
      });
  };

  componentDidMount() {
    this.setState({ isLoading: true, errorMessage: '' });
    this.setItems();
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (nextProps.location.search !== this.props.location.search || nextProps.schoolId !== this.props.schoolId) {
      this.setState({ isLoading: true, errorMessage: '' });
      this.setItems(nextProps);
    }
  }

  setItems(props: Props = this.props) {
    const { history, schoolId } = props;
    const search = parse(history.location.search);
    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;

    let sortDirection: TABLE_SORT_DIRECTION = '';
    let sortColumnsName = '';

    if (typeof search.order === 'string') {
      [sortColumnsName, sortDirection] = search.order.split(':') as [string, TABLE_SORT_DIRECTION];
    }

    const sortByFieldExist = sortColumnsName !== '';
    const sortDirectionExist = sortDirection !== '';
    const filters = getFilters2(COLUMNS, search);
    const isShowFilter = isFilterExist2(filters);
    const isDataFiltered = isFilterExist2(filters);
    const where = getServerFieldSectionWhere(filters);
    const order = sortByFieldExist && sortDirectionExist ? `${sortColumnsName} ${sortDirection}` : 'name ASC';
    const serverQueryFilter = getServerQueryFilter2(page, where, order);

    return BPromise.all([
      getSuperAdminSchoolHousesCount(schoolId, where),
      getSuperAdminSchoolHouses(schoolId, serverQueryFilter)
    ])
      .then(([countObj, items]) => {
        this.setState({
          currentPage: page,
          sortDirection,
          sortColumnsName,
          isShowFilter,
          isDataFiltered,
          filters,
          items,
          total: countObj.count,
          basePath: history.location.pathname,
          isLoading: false,
          errorMessage: ''
        });

        return true;
      })
      .catch(error => {
        console.error('Can not load superadmin school houses', error);
        this.setState({
          isLoading: false,
          errorMessage: 'Can not load houses. Please check the session and try again.'
        });
      });
  }

  setCurrentPageParams = (currentPage: number): void => {
    const search: string[] = [];
    const { filters, sortColumnsName, sortDirection } = this.state;

    if (currentPage !== FIRST_PAGE) {
      search.push(`page=${currentPage}`);
    }

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    if (isFilterExist2(filters)) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onItemClick = (index: number): void => {
    const { items, selectedItems } = this.state;
    const selectedItem = items[index];
    const selectedItemIndex = selectedItems.findIndex(item => selectedItem.id === item.id);
    const selectedItemsUpdated = [...selectedItems];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setState({ selectedItems: selectedItemsUpdated });
  };

  onSelectAllOnPageClick = (): void => {
    const { items, selectedItems } = this.state;
    const itemsFiltered = items.filter(item => selectedItems.every(selectedItem => selectedItem.id !== item.id));

    this.setState({
      selectedItems: [...selectedItems, ...itemsFiltered],
      isSelectAllChecked: true
    });
  };

  onSelectAllClick = (): void => {
    const { selectedItems, filters } = this.state;
    const where = getServerFieldSectionWhere(filters);

    this.setState({ isLoading: true, errorMessage: '' });

    getAllSuperAdminSchoolHouses(this.props.schoolId, where)
      .then(items => {
        const itemsFiltered = items.filter(item => selectedItems.every(selectedItem => selectedItem.id !== item.id));

        this.setState({
          selectedItems: [...selectedItems, ...itemsFiltered],
          isSelectAllChecked: true,
          isLoading: false
        });
      })
      .catch(error => {
        console.error('Can not select all superadmin school houses', error);
        this.setState({
          isLoading: false,
          errorMessage: 'Can not select all houses. Please try again.'
        });
      });
  };

  onUnselectAllClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName, filters } = this.state;
    const order = getOrder(sortField, sortDirection, sortColumnsName);
    const search: string[] = [`order=${sortField}:${order}`];

    if (isFilterExist2(filters)) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.filters;
    const currentFilterField = filters[filterField];
    let nextFilters = { ...filters };

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue
            }
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue
            }
          };
          break;
      }
    } else {
      const filter = COLUMNS.find(col => col.field === filterField);
      const filterType = filter ? filter.type : FILTER_TYPE.TEXT;

      if (filterType === FILTER_TYPE.MULTISELECT) {
        const options = event.target.options;
        const value = [];

        for (let i = 0; i < options.length; i++) {
          if (options[i].selected) {
            value.push(options[i].value);
          }
        }

        nextFilters = {
          ...nextFilters,
          [filterField]: value
        };
      } else {
        nextFilters = {
          ...nextFilters,
          [filterField]: filterValue
        };
      }
    }

    this.setState({ filters: nextFilters });
  };

  onApplyFilterClick = (): void => {
    const { filters, sortDirection, sortColumnsName } = this.state;
    const search: string[] = [];

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    if (isFilterExist2(filters)) {
      search.push(getSearchFilter(filters));
    }

    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onClearFilterClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: ''
    });
  };

  onTableFilterClick = (event): void => {
    event.preventDefault();

    this.setState({
      isShowFilter: !this.state.isShowFilter
    });
  };

  render() {
    const {
      currentPage,
      errorMessage,
      filters,
      isDataFiltered,
      isLoading,
      isSelectAllChecked,
      isShowFilter,
      items,
      selectedItems,
      sortColumnsName,
      sortDirection,
      total
    } = this.state;

    const actionItems: ActionItem[] = [
      {
        itemText: 'View house',
        isActive: false,
        onItemClick: () => undefined
      }
    ];

    if (isLoading) {
      return <Loader />;
    }

    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h4 className="mb-1">Houses</h4>
            <div className="text-muted">Read-only houses list migrated onto the shared Grid2.</div>
          </div>
          <div className="d-flex">
            <button className="btn btn-primary mr-2" onClick={this.openCreateModal}>
              Add house
            </button>
            <button className="btn btn-outline-secondary" onClick={() => this.setItems()}>
              Refresh
            </button>
          </div>
        </div>

        {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}

        <Grid2
          dataItems={items}
          filters={filters}
          currentPage={currentPage}
          total={total}
          isSelectAllChecked={isSelectAllChecked}
          selectedItemsCount={selectedItems.length}
          isDataFiltered={isDataFiltered}
          sortDirection={sortDirection}
          sortColumnsName={sortColumnsName}
          isShowFilter={isShowFilter}
          dataItemsSelected={selectedItems}
          columns={COLUMNS}
          actionItems={actionItems}
          options={{ status: STATUS_OPTIONS }}
          onItemClick={this.onItemClick}
          onSortClick={this.onTableSortClick}
          onApplyFilterClick={this.onApplyFilterClick}
          onClearFilterClick={this.onClearFilterClick}
          onTableFilterChange={this.onTableFilterChange}
          onTableFilterClick={this.onTableFilterClick}
          setCurrentPageParams={this.setCurrentPageParams}
          onSelectAllClick={this.onSelectAllClick}
          onSelectAllOnPageClick={this.onSelectAllOnPageClick}
          onUnselectAllClick={this.onUnselectAllClick}
          isCSV={true}
        />

        <SimpleModal
          isOpen={this.state.isCreateModalOpen}
          title="Add house"
          onCloseClick={this.closeCreateModal}
          showFooter={false}
          dialogStyle={{ maxWidth: 620 }}
          bodyClassName="font-weight-normal"
        >
          {this.state.createErrorMessage && (
            <div className="alert alert-danger">{this.state.createErrorMessage}</div>
          )}

          <div className="form-group">
            <label htmlFor="superAdminHouseName">
              <strong>House name</strong>
            </label>
            <input
              id="superAdminHouseName"
              className="form-control"
              value={this.state.createName}
              onChange={event => this.setState({ createName: event.target.value })}
              disabled={this.state.isCreating}
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="superAdminHouseDescription">
              <strong>Description</strong>
            </label>
            <textarea
              id="superAdminHouseDescription"
              className="form-control"
              rows={3}
              value={this.state.createDescription}
              onChange={event => this.setState({ createDescription: event.target.value })}
              disabled={this.state.isCreating}
            />
          </div>

          <div className="form-group">
            <label>
              <strong>Colours</strong>
            </label>
            <div className="d-flex align-items-center flex-wrap">
              {this.state.createColors.map((color, index) => (
                <div key={`house-create-color-${index}`} className="d-flex align-items-center mr-3 mb-2">
                  <input
                    type="color"
                    value={color}
                    onChange={event => this.onCreateColorChange(index, event.target.value)}
                    disabled={this.state.isCreating}
                    style={{ width: 44, height: 38, padding: 2 }}
                  />
                  <input
                    className="form-control ml-2"
                    value={color}
                    onChange={event => this.onCreateColorChange(index, event.target.value)}
                    disabled={this.state.isCreating}
                    style={{ width: 105 }}
                  />
                  <button
                    type="button"
                    className="btn btn-link text-danger px-2"
                    onClick={() => this.removeCreateColor(index)}
                    disabled={this.state.isCreating}
                    title="Remove colour"
                  >
                    ×
                  </button>
                </div>
              ))}
              {this.state.createColors.length < 2 && (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm mb-2"
                  onClick={this.addCreateColor}
                  disabled={this.state.isCreating}
                >
                  Add colour
                </button>
              )}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="superAdminHousePic">
              <strong>Emblem</strong>
              <span className="text-muted font-weight-normal"> (optional)</span>
            </label>
            <div className="d-flex align-items-center">
              <div
                className="mr-3"
                style={{
                  width: 88,
                  height: 88,
                  border: '1px solid #dee2e6',
                  borderRadius: 8,
                  background: '#f8f9fa',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden'
                }}
              >
                {this.state.createPicPreview || this.state.createPic ? (
                  <img
                    src={this.state.createPicPreview || this.state.createPic}
                    alt="House emblem preview"
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                ) : (
                  <span className="text-muted small">No emblem</span>
                )}
              </div>
              <input
                id="superAdminHousePic"
                type="file"
                className="form-control-file"
                accept="image/*"
                onChange={this.onCreatePicChange}
                disabled={this.state.isCreating}
              />
            </div>
          </div>

          <div className="d-flex justify-content-end mt-4">
            <button
              type="button"
              className="btn btn-secondary mr-2"
              onClick={this.closeCreateModal}
              disabled={this.state.isCreating}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={this.createHouse}
              disabled={this.state.isCreating}
            >
              {this.state.isCreating ? 'Saving…' : 'Create house'}
            </button>
          </div>
        </SimpleModal>
      </div>
    );
  }
}

